import { Tab } from '@mui/material';
import { Box } from '@mui/system';
import React, { MouseEvent, ReactNode, useMemo, useState } from 'react';

import {
  InstructionButton,
  InstructionDialog,
} from '~/components/Instructions';
import { SolaForceTabs } from '~/components/SolaForceTabs';
import { EmployeeModuleTabProps } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/types';

import { useSxStyles } from './useStyles';

export type EmployeeModuleTabsLayoutProps<TabPath> = {
  tabs: EmployeeModuleTabProps<TabPath>[];
  onTabChange?: (tabId: TabPath) => void;
  children?: ReactNode;
};

export const EmployeeModuleTabsLayout = <TabPath,>(
  props: EmployeeModuleTabsLayoutProps<TabPath>,
) => {
  const { tabs, children, onTabChange } = props;
  const sxStyles = useSxStyles();

  const [instruction, setInstruction] = useState<
    { title: string; content: string } | undefined
  >();

  const handleClick = (
    e: MouseEvent<HTMLElement>,
    tabId: TabPath | undefined,
  ) => {
    if (onTabChange && tabId) {
      e.preventDefault();
      onTabChange(tabId);
    }
  };

  const activeTab = useMemo(
    () => tabs.find((tab) => tab.active)?.tabId || false,
    [tabs],
  );

  return (
    <Box sx={sxStyles.root}>
      {tabs.length > 0 && (
        <SolaForceTabs
          value={activeTab}
          background={'transparent'}
          onChange={(_, value) => {
            if (!!onTabChange) {
              onTabChange(value);
            }
          }}
        >
          {tabs.map((props) => {
            const {
              title,
              href,
              disabled,
              tabId,
              tabHelp,
              variant,
              prefetchData,
            } = props;

            return (
              <Tab
                component={'a'}
                className={variant}
                label={
                  <Box component={'span'} sx={sxStyles.tabLabelWrapper}>
                    <Box component={'span'}>{title}</Box>
                    {tabHelp && (
                      <InstructionButton
                        sx={sxStyles.tabHelpButton}
                        onClick={(event: React.SyntheticEvent) => {
                          event.stopPropagation();
                          event.preventDefault();
                          setInstruction({ title, content: tabHelp });
                        }}
                      />
                    )}
                  </Box>
                }
                value={tabId}
                key={`${tabId}`}
                data-cy={tabId}
                onMouseEnter={() => (prefetchData ? prefetchData() : undefined)}
                disabled={disabled}
                onClick={(event: MouseEvent<HTMLElement>) =>
                  handleClick(event, tabId)
                }
                href={href}
              />
            );
          })}
        </SolaForceTabs>
      )}
      <InstructionDialog
        open={!!instruction}
        title={instruction?.title ?? ''}
        richTextContent={instruction?.content}
        onClose={() => setInstruction(undefined)}
      />
      <Box sx={sxStyles.content}>{children}</Box>
    </Box>
  );
};
